import { skipToken, useSuspenseQuery } from "@apollo/client";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import { deepOrange, green, grey } from "@mui/material/colors";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { ChartsReferenceLine } from "@mui/x-charts";
import { AnimatedLine, AnimatedLineProps, LineChart } from "@mui/x-charts/LineChart";
import ChartLegend from "components/Dashboard/ChartLegend";
import { NO_MATCHED_VENDOR_LABEL } from "encamp-shared/src/waste/vendorStatsFormat";
import {
  ManifestDiscrepancyClosureStatsByMonthQuery,
  ManifestDiscrepancyClosureStatsByVendorQuery,
} from "generated-graphql/graphql";
import { useQueryParams } from "hooks/useQueryParams";
import { useTenant } from "hooks/useTenant";
import { useEffect, useMemo, useState } from "react";

import {
  MANIFEST_DISCREPANCY_CLOSURE_STATS_BY_MONTH_QUERY,
  MANIFEST_DISCREPANCY_CLOSURE_STATS_BY_VENDOR_QUERY,
  MANIFEST_DISCREPANCY_CLOSURE_STATS_QUERY,
} from "./api";
import {
  closureDataEmptyMessage,
  CORRECTION_TRACKING_START_YEAR,
  formatDays,
  lastVisibleMonth,
  MONTH_LABELS,
  monthDateRange,
} from "./correctionTracking";
import { removeOmnisearchTerm, upsertOmnisearchTerm } from "./omnisearch";

// No deadline yardstick on this metric: the 30-day window only applies to
// EPA correction requests, missing-manifest discrepancies escalate at 45/60
// days, and field discrepancies have no threshold at all — so the charts
// stay neutral (lower is better) rather than implying a shared deadline.

// x-charts' stock react-spring line animation has a mount bug in this
// dashboard: AnimatedLine reveals the line behind a clip-rect whose width a
// react-spring `useSpring` animates, but when the chart mounts inside this
// page's suspense transition the spring never starts — the clip stays
// collapsed and the line is invisible until some interaction (hover)
// re-renders the line element, which re-evaluates the spring and finally
// kicks it off.
//
// This wrapper automates that kick: one frame after mount it flips a prop on
// the stock AnimatedLine, forcing the same re-render a hover would have
// caused. The spring then runs its normal mount animation.
function KickedAnimatedLine(props: AnimatedLineProps) {
  const [kick, setKick] = useState(0);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setKick(1));
    return () => cancelAnimationFrame(raf);
  }, []);
  // The data-* attr lands on the path element harmlessly — its only job is
  // to change between renders so AnimatedLine can't bail out of re-rendering.
  return <AnimatedLine {...props} data-spring-kick={kick} />;
}

interface SummaryProps {
  shippedYear: number;
  skip?: boolean;
  selectedVendor?: string | null;
  // When set (1-12), the card narrows to that shipped month's stats. The
  // month data is already on hand from the byMonth query, so this narrows
  // client-side with no extra fetch.
  selectedMonth?: number | null;
}

// Overall "days to correction closure" stat: KPI card with the average,
// a delta chip vs the previous month, and best/worst month anchors.
// Rendered inside the unified vendor-stats widget — a parent Suspense
// boundary owns the loading fallback, and the vendor-select handler's
// startTransition keeps the existing card on screen while new data loads.
export function CorrectionClosureSummary({
  shippedYear,
  skip = false,
  selectedVendor = null,
  selectedMonth = null,
}: SummaryProps) {
  const { tenantId } = useTenant();

  // Same query for both modes — passing vendorSource: null returns all-vendor
  // totals; a real vendor narrows the card to just that vendor.
  const { data, error } = useSuspenseQuery(
    MANIFEST_DISCREPANCY_CLOSURE_STATS_QUERY,
    skip || !tenantId
      ? skipToken
      : {
          variables: { tenantId, shippedYear, vendorSource: selectedVendor },
          errorPolicy: "all",
        }
  );
  // Monthly roll-up for the delta chip + best/worst rows. The monthly trend
  // chart beside this card issues the identical query, so Apollo dedupes it —
  // this costs no extra network round-trip.
  const monthQuery = useSuspenseQuery(
    MANIFEST_DISCREPANCY_CLOSURE_STATS_BY_MONTH_QUERY,
    skip || !tenantId
      ? skipToken
      : {
          variables: { tenantId, shippedYear, vendorSource: selectedVendor },
          errorPolicy: "all",
        }
  );

  if (error) {
    return <Typography color="error">Couldn't load closure stats</Typography>;
  }

  const stats = data?.manifestDiscrepancyClosureStats;
  if (!stats) {
    return null;
  }

  if (stats.resolvedCount === 0 || stats.avgDaysToClosure == null) {
    // Pre-2026 the whole year is empty by definition (external tracking), so
    // the epoch message wins even when a vendor filter is active.
    const emptyMessage =
      selectedVendor && shippedYear >= CORRECTION_TRACKING_START_YEAR
        ? `No resolved e-Manifest corrections for ${selectedVendor} in ${shippedYear}.`
        : closureDataEmptyMessage(shippedYear);
    return (
      <Stack spacing={0.5}>
        <Typography variant="h3">—</Typography>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Stack>
    );
  }

  return (
    <ClosureStat
      avgDays={stats.avgDaysToClosure}
      medianDays={stats.medianDaysToClosure ?? null}
      resolvedCount={stats.resolvedCount}
      shippedYear={shippedYear}
      selectedMonth={selectedMonth}
      // Months errors degrade gracefully: the headline still renders, the
      // delta/best/worst extras are simply omitted.
      months={monthQuery.data?.manifestDiscrepancyClosureStatsByMonth ?? []}
    />
  );
}

type MonthStat =
  ManifestDiscrepancyClosureStatsByMonthQuery["manifestDiscrepancyClosureStatsByMonth"][number];

function monthName(month: number): string {
  return MONTH_LABELS[month - 1] ?? String(month);
}

function ClosureStat({
  avgDays,
  medianDays,
  resolvedCount,
  shippedYear,
  selectedMonth,
  months,
}: {
  avgDays: number;
  medianDays: number | null;
  resolvedCount: number;
  shippedYear: number;
  selectedMonth: number | null;
  months: readonly MonthStat[];
}) {
  // Only months that actually have closures participate in the headline,
  // delta, and best/worst rows — empty months are gaps, not zeros.
  const withData = months
    .filter((m): m is MonthStat & { avgDaysToClosure: number } => m.avgDaysToClosure != null)
    .sort((a, b) => a.month - b.month);

  // Month selection narrows the headline to that month's stats. The delta
  // then compares against the closest earlier month with data, so the chip
  // answers "better or worse than the month before?" for the selection too.
  const selectedRow = selectedMonth ? withData.find((m) => m.month === selectedMonth) : undefined;
  const current = selectedRow ?? withData[withData.length - 1];
  const previous = current
    ? [...withData].reverse().find((m) => m.month < current.month)
    : undefined;
  const delta = current && previous ? current.avgDaysToClosure - previous.avgDaysToClosure : null;

  let best: (typeof withData)[number] | undefined;
  let worst: (typeof withData)[number] | undefined;
  for (const m of withData) {
    if (!best || m.avgDaysToClosure < best.avgDaysToClosure) best = m;
    if (!worst || m.avgDaysToClosure > worst.avgDaysToClosure) worst = m;
  }

  // A selected month can be empty for the current vendor narrowing — keep the
  // card's frame and say so rather than showing a stale year number.
  if (selectedMonth && !selectedRow) {
    return (
      <Stack spacing={0.5} sx={{ flexGrow: 1, justifyContent: "center" }}>
        <Typography variant="h3">—</Typography>
        <Typography variant="body2" color="text.secondary">
          No resolved e-Manifest corrections for {monthName(selectedMonth)} {shippedYear}.
        </Typography>
      </Stack>
    );
  }

  const headlineAvg = selectedRow ? selectedRow.avgDaysToClosure : avgDays;
  const headlineMedian = selectedRow ? (selectedRow.medianDaysToClosure ?? null) : medianDays;
  const headlineCount = selectedRow ? selectedRow.resolvedCount : resolvedCount;
  const periodLabel = selectedMonth
    ? `${monthName(selectedMonth)} ${shippedYear}`
    : `${shippedYear}`;

  return (
    // flexGrow + justifyContent vertically center the card against the taller
    // monthly-trend chart beside it, mirroring the compliance donut column.
    <Stack spacing={1.5} sx={{ flexGrow: 1, justifyContent: "center" }}>
      <Stack spacing={0.5}>
        <Typography variant="h2" component="div">
          {formatDays(headlineAvg)}
          <Typography variant="h5" component="span" color="text.secondary" sx={{ ml: 1 }}>
            days
          </Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          avg time to closure
          {/* Median alongside the mean exposes outlier skew at a glance —
              a large gap means a few stragglers are dragging the average. */}
          {headlineMedian != null && ` · median ${formatDays(headlineMedian)} days`}
        </Typography>
      </Stack>
      {delta !== null && previous && <DeltaChip delta={delta} previousMonth={previous.month} />}
      <Divider>
        <Typography variant="caption" color="text.secondary">
          {headlineCount} resolved · {periodLabel}
        </Typography>
      </Divider>
      {/* Best/worst anchor the average inside the year's actual range — only
          shown for the year view (a single month has no range) and only
          meaningful once two months differ. */}
      {!selectedMonth && best && worst && best.month !== worst.month && (
        <Stack spacing={0.5}>
          <MonthAnchorRow label="Best month" stat={best} />
          <MonthAnchorRow label="Worst month" stat={worst} />
        </Stack>
      )}
    </Stack>
  );
}

// Down is better for closure time: an improving (negative) delta gets the
// waste-overview palette's green, a worsening one its deep orange — same
// hues the compliance donut uses for good/bad.
function DeltaChip({ delta, previousMonth }: { delta: number; previousMonth: number }) {
  const improving = delta < 0;
  const flat = Math.abs(delta) < 0.05;
  const label = flat
    ? `no change vs ${monthName(previousMonth)}`
    : `${formatDays(Math.abs(delta))} days vs ${monthName(previousMonth)}`;

  let icon;
  if (!flat) {
    icon = improving ? <ArrowDownward fontSize="small" /> : <ArrowUpward fontSize="small" />;
  }
  let chipSx: { backgroundColor: string; color: string } = {
    backgroundColor: "action.selected",
    color: "text.primary",
  };
  if (!flat) {
    chipSx = improving
      ? { backgroundColor: green[50], color: green[800] }
      : { backgroundColor: deepOrange[50], color: deepOrange[800] };
  }

  return (
    <Stack direction="row">
      <Chip
        size="small"
        icon={icon}
        label={label}
        sx={{ ...chipSx, "& .MuiChip-icon": { color: "inherit" } }}
      />
    </Stack>
  );
}

function MonthAnchorRow({
  label,
  stat,
}: {
  label: string;
  stat: MonthStat & { avgDaysToClosure: number };
}) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">
        {monthName(stat.month)} · {formatDays(stat.avgDaysToClosure)} days
      </Typography>
    </Stack>
  );
}

// The two views the parent can ask this panel to render — mirrors
// VendorBreakdownTile so the dashboard composes both metrics the same way.
interface BreakdownProps {
  shippedYear: number;
  view: "byVendor" | "byMonth";
  skip?: boolean;
  // When set, the vendor table highlights this row and the monthly line chart
  // overlays this vendor's trend on top of the all-vendor line.
  selectedVendor?: string | null;
  onSelectVendor?: (vendor: string | null) => void;
  // When set, the vendor table narrows to that shipped month and the monthly
  // chart marks the selection. Clicking a chart point toggles it.
  selectedMonth?: number | null;
  onSelectMonth?: (month: number | null) => void;
  /**
   * Whether the host's manifest grid can filter on the date a discrepancy was
   * raised. When it can't, a month click still selects the month — the chip,
   * the reference line, and the month-narrowed vendor table all still work —
   * but it emits no omnisearch change, rather than dropping the filters the
   * grid can't narrow. Defaults to true.
   */
}

export function CorrectionClosureBreakdown({
  shippedYear,
  view,
  skip = false,
  selectedVendor = null,
  onSelectVendor,
  selectedMonth = null,
  onSelectMonth,
}: BreakdownProps) {
  const { tenantId } = useTenant();
  const { setFilters } = useQueryParams<{ omnisearch?: string }>();

  // All queries are Suspense-based: parent Suspense boundaries own the
  // loading fallback, and startTransition keeps existing data on screen
  // while new data loads after a vendor selection.
  const vendorQuery = useSuspenseQuery(
    MANIFEST_DISCREPANCY_CLOSURE_STATS_BY_VENDOR_QUERY,
    skip || !tenantId || view !== "byVendor"
      ? skipToken
      : {
          variables: { tenantId, shippedYear, shippedMonth: selectedMonth },
          errorPolicy: "all",
        }
  );
  // All-vendor monthly trend — the line the per-vendor overlay sits on.
  const monthQuery = useSuspenseQuery(
    MANIFEST_DISCREPANCY_CLOSURE_STATS_BY_MONTH_QUERY,
    skip || !tenantId || view !== "byMonth"
      ? skipToken
      : { variables: { tenantId, shippedYear, vendorSource: null }, errorPolicy: "all" }
  );
  // Per-vendor monthly trend — only fetched when a vendor is selected.
  const vendorMonthQuery = useSuspenseQuery(
    MANIFEST_DISCREPANCY_CLOSURE_STATS_BY_MONTH_QUERY,
    skip || !tenantId || view !== "byMonth" || !selectedVendor
      ? skipToken
      : {
          variables: { tenantId, shippedYear, vendorSource: selectedVendor },
          errorPolicy: "all",
        }
  );

  const handleVendorClick = (vendor: string) => {
    const togglingOff = vendor === selectedVendor;
    setFilters((prev) => ({
      ...prev,
      // Toggling narrows the grid to this vendor (or removes the term on
      // toggle-off); the year scope stays the year picker's hidden default.
      omnisearch: togglingOff
        ? removeOmnisearchTerm(prev.omnisearch ?? "", "vendor")
        : upsertOmnisearchTerm(prev.omnisearch ?? "", "vendor", vendor),
    }));
    // …and toggle the vendor selection that drives the chart overlay.
    onSelectVendor?.(togglingOff ? null : vendor);
  };

  const handleMonthClick = (month: number) => {
    // Narrow the manifest grid to this month by the discrepancy raised date —
    // the point's own axis. The raised-date term supersedes the grid's hidden
    // shipped-year default (see manifestsV3DefaultSearch.ts), keeping cross-year rows
    // without a visible shippedDate override.
    setFilters((prev) => ({
      ...prev,
      omnisearch: upsertOmnisearchTerm(
        prev.omnisearch ?? "",
        "closureRaisedAt",
        monthDateRange(shippedYear, month)
      ),
    }));
    // …and toggle the month selection that narrows the summary card and
    // vendor table (mirrors the vendor row click behaviour). This half is the
    // chart's own state, so it runs on every host.
    onSelectMonth?.(month === selectedMonth ? null : month);
  };

  return (
    <>
      {view === "byVendor" && (
        <VendorClosureTable
          shippedYear={shippedYear}
          error={vendorQuery.error}
          data={vendorQuery.data}
          selectedVendor={selectedVendor}
          selectedMonth={selectedMonth}
          onVendorClick={handleVendorClick}
        />
      )}
      {view === "byMonth" && (
        <MonthlyClosureLine
          shippedYear={shippedYear}
          error={monthQuery.error}
          data={monthQuery.data}
          vendorData={vendorMonthQuery.data}
          selectedVendor={selectedVendor}
          selectedMonth={selectedMonth}
          onMonthClick={handleMonthClick}
        />
      )}
    </>
  );
}

type VendorRow =
  ManifestDiscrepancyClosureStatsByVendorQuery["manifestDiscrepancyClosureStatsByVendor"][number];

function VendorClosureTable({
  shippedYear,
  error,
  data,
  selectedVendor,
  selectedMonth,
  onVendorClick,
}: {
  shippedYear: number;
  error: unknown;
  data: ManifestDiscrepancyClosureStatsByVendorQuery | undefined;
  selectedVendor: string | null;
  selectedMonth: number | null;
  onVendorClick: (vendor: string) => void;
}) {
  if (error) {
    return <Typography color="error">Couldn't load vendor closure stats</Typography>;
  }

  const rows = data?.manifestDiscrepancyClosureStatsByVendor ?? [];
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {selectedMonth && shippedYear >= CORRECTION_TRACKING_START_YEAR
          ? `No resolved e-Manifest corrections for ${monthName(selectedMonth)} ${shippedYear}.`
          : closureDataEmptyMessage(shippedYear)}
      </Typography>
    );
  }

  return (
    // Tenants with many vendors can produce a long list; cap the table at
    // roughly six rows and scroll internally (sticky header) instead of
    // letting it eat the page's vertical space.
    <TableContainer
      sx={{
        maxHeight: "16rem",
        // stickyHeader cells default to background.default (grey); match
        // the tile's paper background instead.
        "& .MuiTableCell-stickyHeader": { backgroundColor: "background.paper" },
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          {/* Mirrors the compliance team's spreadsheet layout: avg for the
              headline, median to expose outlier skew (avg 38 / median 17 is
              an outlier problem, not a habit problem), min/max for range. */}
          <TableRow>
            <TableCell>Vendor</TableCell>
            <TableCell align="right">Avg days</TableCell>
            <TableCell align="right">Median days</TableCell>
            <TableCell align="right">Min</TableCell>
            <TableCell align="right">Max</TableCell>
            <TableCell align="right">Resolved</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <VendorClosureRow
              key={row.vendor ?? `__unmatched-${index}`}
              row={row}
              selected={row.vendor !== null && row.vendor === selectedVendor}
              onVendorClick={onVendorClick}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function VendorClosureRow({
  row,
  selected,
  onVendorClick,
}: {
  row: VendorRow;
  selected: boolean;
  onVendorClick: (vendor: string) => void;
}) {
  const theme = useTheme();
  const vendor = row.vendor;
  const clickable = vendor !== null && vendor !== undefined;

  // Selected state: MUI's `selected` adds a subtle background tint; the left
  // border accent + bold vendor name make it unmistakable at a glance.
  const selectedSx = selected
    ? {
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        "& > *:first-of-type": { fontWeight: 600 },
      }
    : {
        borderLeft: "4px solid transparent",
      };

  return (
    <TableRow
      hover={clickable}
      selected={selected}
      onClick={clickable ? () => onVendorClick(vendor) : undefined}
      sx={{
        ...selectedSx,
        ...(clickable ? { cursor: "pointer" } : {}),
      }}
    >
      <TableCell>
        {clickable ? (
          vendor
        ) : (
          <Typography variant="body2" color="text.secondary" component="span">
            {NO_MATCHED_VENDOR_LABEL}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">{formatDays(row.avgDaysToClosure)}</TableCell>
      <TableCell align="right">{formatDays(row.medianDaysToClosure)}</TableCell>
      <TableCell align="right">{formatDays(row.minDaysToClosure)}</TableCell>
      <TableCell align="right">{formatDays(row.maxDaysToClosure)}</TableCell>
      <TableCell align="right">{row.resolvedCount}</TableCell>
    </TableRow>
  );
}

type MonthRow =
  ManifestDiscrepancyClosureStatsByMonthQuery["manifestDiscrepancyClosureStatsByMonth"][number];

function MonthlyClosureLine({
  shippedYear,
  error,
  data,
  vendorData,
  selectedVendor,
  selectedMonth,
  onMonthClick,
}: {
  shippedYear: number;
  error: unknown;
  data: ManifestDiscrepancyClosureStatsByMonthQuery | undefined;
  vendorData?: ManifestDiscrepancyClosureStatsByMonthQuery | undefined;
  selectedVendor: string | null;
  selectedMonth: number | null;
  /** Always wired — the month selection is the chart's own state. What the
   *  click does beyond that (grid filter or not) is the breakdown's call. */
  onMonthClick: (month: number) => void;
}) {
  const theme = useTheme();
  // Draw every visible month (Jan through the last elapsed month) on the
  // axis; months with no resolved discrepancies render as gaps in the line
  // (null), not zeros — zero would read as "instant closure".
  const dataset = useMemo(() => {
    const byMonth = new Map<number, MonthRow>();
    (data?.manifestDiscrepancyClosureStatsByMonth ?? []).forEach((row) => {
      byMonth.set(row.month, row);
    });
    const vendorByMonth = new Map<number, MonthRow>();
    (vendorData?.manifestDiscrepancyClosureStatsByMonth ?? []).forEach((row) => {
      vendorByMonth.set(row.month, row);
    });
    const visibleMonths = lastVisibleMonth(shippedYear, [
      ...byMonth.keys(),
      ...vendorByMonth.keys(),
    ]);
    return MONTH_LABELS.slice(0, visibleMonths).map((label, i) => {
      const month = i + 1;
      const row = byMonth.get(month);
      const vendorRow = vendorByMonth.get(month);
      return {
        month,
        monthLabel: label,
        avgDays: row?.avgDaysToClosure ?? null,
        medianDays: row?.medianDaysToClosure ?? null,
        resolvedCount: row?.resolvedCount ?? 0,
        vendorAvgDays: vendorRow?.avgDaysToClosure ?? null,
        vendorMedianDays: vendorRow?.medianDaysToClosure ?? null,
        vendorResolvedCount: vendorRow?.resolvedCount ?? 0,
      };
    });
  }, [data, vendorData, shippedYear]);

  if (error) {
    return <Typography color="error">Couldn't load monthly closure trend</Typography>;
  }

  const hasData = dataset.some((d) => d.avgDays !== null || d.vendorAvgDays !== null);

  // When a vendor is selected the all-vendor line steps back to gray context
  // and the vendor's own trend takes the accent color — same focus/backdrop
  // relationship as the compliance chart's fg/bg bar stacks.
  const allVendorsColor = selectedVendor ? grey[400] : theme.palette.primary.main;
  const vendorColor = theme.palette.primary.main;

  // Series structure stays stable across vendor selection (the vendor series
  // is just all-null when nothing is selected) so the chart transitions values
  // instead of re-mounting lines.
  const series = [
    {
      id: "allVendors",
      label: "All vendors",
      data: dataset.map((d) => d.avgDays),
      color: allVendorsColor,
      connectNulls: true,
      valueFormatter: (val: number | null, context: { dataIndex: number }) => {
        if (val == null) return "";
        const row = dataset[context.dataIndex];
        const median = row?.medianDays != null ? ` · ${formatDays(row.medianDays)} median` : "";
        return `${formatDays(val)} avg${median} (${row?.resolvedCount ?? 0} resolved)`;
      },
    },
    {
      id: "vendor",
      label: selectedVendor ?? "",
      data: dataset.map((d) => (selectedVendor ? d.vendorAvgDays : null)),
      color: vendorColor,
      connectNulls: true,
      valueFormatter: (val: number | null, context: { dataIndex: number }) => {
        if (val == null) return "";
        const row = dataset[context.dataIndex];
        const median =
          row?.vendorMedianDays != null ? ` · ${formatDays(row.vendorMedianDays)} median` : "";
        return `${formatDays(val)} avg${median} (${row?.vendorResolvedCount ?? 0} resolved)`;
      },
    },
  ];

  const legendItems = [
    { color: allVendorsColor, value: "All vendors" },
    ...(selectedVendor ? [{ color: vendorColor, value: selectedVendor }] : []),
  ];

  // Band value for the selected-month reference line; null when the selected
  // month is off the visible axis (shouldn't happen — selection comes from
  // clicking a rendered point — but guard anyway).
  const selectedMonthLabel = selectedMonth
    ? (dataset.find((d) => d.month === selectedMonth)?.monthLabel ?? null)
    : null;

  if (!hasData) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: theme.spacing(40) }}>
        <Typography variant="body2" color="text.secondary">
          {closureDataEmptyMessage(shippedYear)}
        </Typography>
      </Stack>
    );
  }

  // Shared by the mark and the axis, the chart's two ways into the same month.
  // Months with no closures are gaps in the line, so a click there selects
  // nothing.
  const clickMonth = (dataIndex: number | null | undefined) => {
    const row = dataset[dataIndex ?? -1];
    if (row && (row.resolvedCount > 0 || row.vendorResolvedCount > 0)) {
      onMonthClick(row.month);
    }
  };

  return (
    <Stack spacing={1.5} sx={{ position: "relative" }}>
      <ChartLegend
        config={{ direction: "row", position: "above" }}
        items={legendItems}
        sx={{ paddingLeft: 6 }}
      />
      <Box height={theme.spacing(40)} sx={{ "& .MuiMarkElement-root": { cursor: "pointer" } }}>
        <LineChart
          dataset={dataset}
          series={series}
          xAxis={[
            {
              scaleType: "point",
              dataKey: "monthLabel",
            },
          ]}
          yAxis={[
            {
              scaleType: "linear",
              min: 0,
              label: "Avg days to closure",
            },
          ]}
          grid={{ horizontal: true }}
          slotProps={{ legend: { hidden: true } }}
          // Stock react-spring animation, plus the post-mount kick that
          // makes it actually start — see KickedAnimatedLine above.
          slots={{ line: KickedAnimatedLine }}
          // Marks are the click targets for the month selection + grid
          // filter, so keep them visible even on the dense all-vendor line.
          onMarkClick={(_event, item) => clickMonth(item.dataIndex)}
          onAxisClick={(_event, axisData) => clickMonth(axisData?.dataIndex)}
        >
          {/* Vertical marker on the selected month — the chart-side echo of
              the header chip, so the active narrowing is visible where the
              click happened. (Static SVG, no spring — safe on mount.) */}
          {selectedMonthLabel && (
            <ChartsReferenceLine
              x={selectedMonthLabel}
              lineStyle={{
                stroke: theme.palette.primary.main,
                strokeDasharray: "4 4",
                strokeOpacity: 0.6,
              }}
            />
          )}
        </LineChart>
      </Box>
    </Stack>
  );
}
